<template>
  <v-form>
    <v-container fluid>
      <v-row>
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="value.name"
            :error="errors.name !== null"
            :error-messages="errors.name"
            :counter="60"
            label="Organization name"
            clearable
            required
          ></v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            label="Email"
            v-model="value.email"
            :error="errors.email !== null"
            :error-messages="errors.email"
            :rules="emailRules"
            required
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          md="4"
        >
          <v-combobox
            :value="value.hqAddress"
            :items="searchResults.hqAddress.displayValues"
            :error="errors.hqAddress !== null"
            :error-messages="errors.hqAddress"
            :search-input.sync="hqAddressSearchTerm"
            @input="handleHqAddressChange"
            label="HQ Address"
            hide-no-data
            clearable
            required
          ></v-combobox>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="value.unit"
            label="Unit (optional)"
            clearable
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-combobox
            :value="value.city"
            :items="searchResults.city.displayValues"
            :error="errors.city !== null"
            :error-messages="errors.city"
            :search-input.sync="citySearchTerm"
            @input="handleCityChange"
            label="City"
            hide-no-data
            clearable
            required
          ></v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          md="4"
        >
          <v-combobox
            v-model="value.country"
            :items="searchResults.country.displayValues"
            :error="errors.country !== null"
            :error-messages="errors.country"
            :search-input.sync="countrySearchTerm"
            label="Country"
            hide-no-data
            clearable
            required
          ></v-combobox>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="value.state"
            :counter="10"
            :error="errors.state !== null"
            :error-messages="errors.state"
            label="State"
            clearable
            required
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="value.zipCode"
            :error="errors.zipCode !== null"
            :error-messages="errors.zipCode"
            label="Zip Code"
            clearable
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-checkbox
        v-model="value.saveForFuture"
        label="Save organization information for future transactions"
        required
      ></v-checkbox>
    </v-container>
  </v-form>
</template>

<script>
export default {
  name: 'GoogleAddressForm',
  components: {
  },
  props: {
    value: {
      type: Object,
    },
    errors: {
      type: Object,
    }
  },
  data() {
    return {
      searchResults: {
        city: {displayValues: [], originalValues: []},
        hqAddress: {displayValues: [], originalValues: []},
        country: {displayValues: [], originalValues: []},
      },
      citySearchTerm: '',
      hqAddressSearchTerm: '',
      countrySearchTerm: '',
      emailRules: [
        (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid'
      ],
      requiredRule: [
        (v) => !!v || 'Required',
      ]
    }
  },
  metaInfo () {
    return {
      script: [{
        src: `https://maps.googleapis.com/maps/api/js?key=AIzaSyDUCOZkQSJrvWTqmOxchMF1YfHRff4ZvQk&libraries=places`,
        async: true,
        defer: true,
        callback: () => this.mapsInit(),
      }],
    };
  },
  methods: {
    mapsInit() {
      this.mapService = new window.google.maps.places.AutocompleteService();
    },
    displaySuggestions(predictions, status, fieldName) {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
        this.searchResults[fieldName] = {displayValues: [], originalValues: []};
        return;
      }
      this.searchResults[fieldName] = {displayValues: predictions.map(el => el.description), originalValues: predictions};
    },
    handleHqAddressChange(newValue) {
      const { city, state, country } = this.value;
      const selectedOption = this.searchResults.hqAddress?.originalValues?.find(({ description }) => description === newValue);
      this.hqAddressSearchTerm = '';
      this.value.hqAddress = selectedOption?.structured_formatting?.main_text ?? newValue;
      this.value.city = selectedOption?.terms?.[2]?.value ?? city;
      this.value.state = selectedOption?.terms?.[3]?.value ?? state;
      this.value.country = selectedOption?.terms?.[4]?.value ?? country;
    },
    handleCityChange(newValue) {
      const { state, country } = this.value;
      const selectedOption = this.searchResults.city?.originalValues?.find(({ description }) => description === newValue);
      this.citySearchTerm = '';
      this.value.city = selectedOption?.structured_formatting?.main_text ?? newValue;
      this.value.state = selectedOption?.terms?.[1]?.value ?? state;
      this.value.country = selectedOption?.terms?.[2]?.value ?? country;
    },
  },
  watch: {
    value: {
      handler() {
        this.$emit('input', this.value);
      },
      deep: true,
    },
    citySearchTerm(value) {
        let searchTerm = '';
        if (value !== null && value !== undefined) searchTerm = value;

        this.mapService?.getPlacePredictions({
          input: searchTerm,
          types: ['(cities)'],
        }, (predictions, status) => this.displaySuggestions(predictions, status, 'city'));
    },
    hqAddressSearchTerm(value) {
      let searchTerm = '';
      if (value !== null && value !== undefined) searchTerm = value;

      this.mapService?.getPlacePredictions({
        input: searchTerm,
      }, (predictions, status) => this.displaySuggestions(predictions, status, 'hqAddress'));
    },
    countrySearchTerm(value) {
      let searchTerm = '';
      if (value !== null && value !== undefined) searchTerm = value;

      this.mapService?.getPlacePredictions({
        input: searchTerm,
        types: ['country']
      }, (predictions, status) => this.displaySuggestions(predictions, status, 'country'));
    }
  },

}
</script>

<style lang="scss" scoped>

</style>
